import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import axios from 'axios';
import serverURL from '../config';

/**
 * CandidateSelect Component
 * Searchable dropdown for selecting candidates not yet registered in an event
 * 
 * Props:
 *   - eventId: ID of the current event
 *   - value: Currently selected candidate object
 *   - onChange: Callback when candidate is selected
 *   - isDisabled: Boolean to disable the select
 *   - isClearable: Boolean to allow clearing selection
 */
const CandidateSelect = ({ eventId, value, onChange, isDisabled = false, isClearable = true }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState([]);

    /**
     * Fetch candidates from API based on search query
     */
    const fetchCandidates = useCallback(async (query) => {
        if (!eventId) {
            console.warn('Event ID not provided to CandidateSelect');
            return;
        }

        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                eventId: eventId,
                ...(query && { query })
            });

            const response = await axios.get(
                `${serverURL}/candidates/search?${params.toString()}`
            );

            // Transform API response to react-select format
            const formattedOptions = response.data.map(candidate => ({
                value: candidate.id,
                label: `${candidate.name} (Email: ${candidate.email})`,
                candidate: candidate // Store full candidate object for later use
            }));

            setOptions(formattedOptions);
        } catch (error) {
            console.error('Error fetching candidates:', error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        fetchCandidates('');
    }, [fetchCandidates]);

    /**
     * Handle input change with debouncing
     */
    const handleInputChange = (input) => {
        // Debounce API calls
        if (input.length >= 1) {
            fetchCandidates(input);
        } else {
            fetchCandidates('');
        }
    };

    /**
     * Handle candidate selection
     */
    const handleChange = (selectedOption) => {
        if (selectedOption) {
            // Pass full candidate data to parent component
            onChange({
                candidate_id: selectedOption.value,
                name: selectedOption.candidate.name,
                email: selectedOption.candidate.email,
                mobile: selectedOption.candidate.mobile || '',
                selectedValue: selectedOption
            });
        } else {
            onChange(null);
        }
    };

    const getOptionBackgroundColor = (isSelected, isFocused) => {
        if (isSelected) return '#3b82f6';
        if (isFocused) return '#e0e7ff';
        return 'white';
    };

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
            '&:hover': {
                borderColor: '#3b82f6'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: getOptionBackgroundColor(state.isSelected, state.isFocused),
            color: state.isSelected ? 'white' : 'black',
            cursor: 'pointer',
            '&:active': {
                backgroundColor: '#3b82f6'
            }
        }),
        menu: (provided) => ({
            ...provided,
            maxHeight: '300px',
            zIndex: 9999
        })
    };

    return (
        <Select
            inputId="candidate-select"
            options={options}
            value={value?.selectedValue || null}
            onChange={handleChange}
            onInputChange={handleInputChange}
            isLoading={isLoading}
            isDisabled={isDisabled}
            isClearable={isClearable}
            isSearchable={true}
            placeholder="Search by name or ID..."
            noOptionsMessage={() => 'No candidates found'}
            styles={customStyles}
            classNamePrefix="candidate-select"
        />
    );
};

CandidateSelect.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.object,
    isDisabled: PropTypes.bool,
    isClearable: PropTypes.bool
};

export default CandidateSelect;
